import { useEffect, useRef, useState, type JSX } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useLocale } from '@gs/i18n/localeContext';
import { useDeploymentSlot } from '../hooks/useDeploymentSlot';
import { lireAppKey } from '../hooks/useBuildInfo';
import { GsBuildStamp } from './GsBuildStamp';

/**
 * Hauteur du bandeau, publiee sur `<html>` : `GsLayout` la retranche de sa hauteur d ecran
 * pour que le bandeau pousse la page au lieu de la faire deborder. Absente = 0.
 */
export const GS_TOP_BANNER_HEIGHT_VAR = '--gs-top-banner-height';

// Copie interne FR/EN (precedent AccessGate) : le MEME avertissement dans toutes les apps,
// La consigne : « vraies données, rien ne sort vers les employés » — parole de David du 2026-10-29
// vers 10 h 57 (option b, lot feat-la-copie-coupe-ses-sorties), qui remplace « ne travaillez pas
// ici » (2026-09-29) : la copie devient l endroit ou l on travaille, et ses sorties sont coupees.
// sans une cle de catalogue par app. Les props priment ; zero t() dans @gs/ui.
const COPY = {
  fr: {
    label: 'PRÉPRODUCTION',
    detail: 'vraies données, rien ne sort vers les employés',
    titlePrefix: '[PRÉPROD]',
    copie: 'COPIE',
  },
  en: {
    label: 'PRE-PRODUCTION',
    detail: 'real data, nothing goes out to employees',
    titlePrefix: '[PRE-PROD]',
    copie: 'COPY',
  },
} as const;

export interface GsPreproductionBannerProps {
  /** Mot d alerte, en capitales. Defaut : copie interne selon la locale. */
  label?: string;
  /** La consigne qui suit le mot d alerte. Defaut : copie interne selon la locale. */
  detail?: string;
  /** Prefixe du titre de l onglet. Defaut : copie interne selon la locale. */
  titlePrefix?: string;
}

/**
 * Le debut du titre d onglet. L app connue (`appKey` de `/api/version`) et aucun prefixe impose :
 * `[COPIE gest] ` — l onglet dit de quelle copie il vient (lot feat-la-copie-coupe-ses-sorties,
 * David 2026-10-29 : « il va falloir que ca soit claire ce sur quel copie sa sort »). Sinon le
 * prefixe de toujours.
 */
function debutDuTitre(prefixe: string, prefixeImpose: boolean, motCopie: string, app: string | null): string {
  if (app && !prefixeImpose) return `[${motCopie} ${app}] `;
  return `${prefixe} `;
}

/**
 * Bandeau SYSTEME pleine largeur (UI_STANDARDS, surface « Bandeau », exception « status
 * strip » : fond teinte cran 100) affiche en haut de chaque ecran quand la page a ete
 * servie par l emplacement `preprod`. Decision de David du 2026-09-29 : impossible a rater,
 * signal distinct de la marque (ambre, jamais le rouge GS), titre d onglet prefixe.
 *
 * Monte UNE fois par app, au-dessus de tout l UI (connexion et refus compris), dans
 * `main.tsx`. En production il ne rend rien et ne touche ni le titre ni la mise en page.
 */
export function GsPreproductionBanner({
  label,
  detail,
  titlePrefix,
}: GsPreproductionBannerProps): JSX.Element | null {
  const slot = useDeploymentSlot();
  const localeCtx = useLocale();
  const copy = COPY[localeCtx?.locale === 'en' ? 'en' : 'fr'];
  const active = slot === 'preprod';
  const prefix = titlePrefix ?? copy.titlePrefix;
  const ref = useRef<HTMLDivElement>(null);
  // De quelle copie : la cle de l app, de la MEME lecture de `/api/version` (aucun second appel).
  const [app, setApp] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void lireAppKey().then((cle) => {
      if (!cancelled) setApp(cle);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  // Titre de l onglet : prefixe, et re-prefixe si l app le change ensuite.
  useEffect(() => {
    if (!active) return;
    const lead = debutDuTitre(prefix, titlePrefix !== undefined, copy.copie, app);
    const apply = () => {
      if (!document.title.startsWith(lead)) document.title = lead + document.title;
    };
    apply();
    const observer = typeof MutationObserver === 'undefined' ? null : new MutationObserver(apply);
    observer?.observe(document.head, { childList: true, characterData: true, subtree: true });
    return () => {
      observer?.disconnect();
      if (document.title.startsWith(lead)) document.title = document.title.slice(lead.length);
    };
  }, [active, prefix, titlePrefix, copy.copie, app]);

  // Hauteur publiee pour GsLayout (le texte peut passer sur deux lignes au telephone).
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(GS_TOP_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(GS_TOP_BANNER_HEIGHT_VAR);
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      ref={ref}
      role="status"
      data-gs-preproduction-banner=""
      className="flex w-full items-center justify-center gap-2 border-b-2 border-amber-500 bg-amber-100 px-4 py-1.5 text-center text-field text-amber-900 dark:border-amber-500 dark:bg-amber-900 dark:text-amber-50"
    >
      <AlertTriangle size={15} aria-hidden="true" className="shrink-0" />
      <p>
        {app && (
          <>
            <strong className="font-bold tracking-wide" data-gs-copie-de={app}>
              {copy.copie} {app}
            </strong>
            {' · '}
          </>
        )}
        <strong className="font-bold tracking-wide">{label ?? copy.label}</strong>
        {' — '}
        <span className="font-medium">{detail ?? copy.detail}</span>
        {/* La MEME version que le pied du menu en production (GsBuildStamp) : on compare d un coup d oeil. */}
        <GsBuildStamp lead=" · " />
      </p>
    </div>
  );
}
